import { Ref } from 'vue';
import { httpClient } from '../../core/axios/http-client';
import {
  ChunksPayload,
  IDirectoryContentRequest,
  IDirectoryItem,
  IIndexesState,
  ILibrary,
  Index,
  IndexDocument,
  IndexDocumentAndChunks,
  IndexerConfigurationModel,
  IndexIdLite,
  IndexLite,
} from './models';
import { ResolveParams, ResolveResult } from '@/core/models/routing';
import { useUiStore } from '..';
import useContainerFiltersResolveParams from '@/core/composable/containers/use-container-filters-resolve-params';

import { IPagedList } from '../core/models';
import { SharePointDeletePayload } from '../chat/models';
import { storeToRefs } from 'pinia';
import { Sentence } from '../ui/models';

const useIndexesStoreActions = (state: Ref<IIndexesState>) => {
  const API_PREFIX = '/indexes';
  const { buildResolveParamsFiltersUsingId } = useContainerFiltersResolveParams();
  const { sentenceList } = storeToRefs(useUiStore());

  const getIndexes = (resolveParams: ResolveParams) => {
    const filters = buildResolveParamsFiltersUsingId(resolveParams);
    // Prevent pagination flicker during API call
    if (filters.pagination) {
      filters.pagination.rowsNumber = 0;
    }
    return httpClient.post<void, IPagedList<Index>>(`${API_PREFIX}`, filters).then((response) => {
      state.value.indexes = response.dataList;
      return new ResolveResult(filters.pagination, response, resolveParams);
    });
  };

  const fetchDirectoryContent = async (params: IDirectoryContentRequest) => {
    return await httpClient.get<void, IDirectoryItem[]>(`${API_PREFIX}/directory-items`, {
      params,
    });
  };

  const getIndexById = (resolveParams: ResolveParams) => {
    return httpClient.get<void, Index>(`${API_PREFIX}/${resolveParams.params.id}`).then(async (response) => {
      await getLibrary(response.driveId);
      state.value.IndexProperties = response;
      return response; // Return the response so it can be used by calling code
    });
  };

  const getLibrary = (driveId: string) => {
    return httpClient
      .get<void, ILibrary>(`${API_PREFIX}/library`, {
        params: {
          driveId,
        },
      })
      .then((resp) => {
        state.value.libraryOnFocus = resp;
      });
  };

  const getLibraries = () => {
    return httpClient.get<void, ILibrary[]>(`${API_PREFIX}/libraries`).then((resp) => {
      state.value.libraries = resp;
    });
  };

  const sendIndexerConfiguration = async () => {
    return httpClient.post<void, string>(`${API_PREFIX}/start-indexer`, state.value.sourceToIndex).then((data) => {
      return data;
    });
  };

  const getIndexesIds = () => {
    return httpClient.get<void, IndexIdLite[]>(`${API_PREFIX}/indexes-ids`).then((resp) => {
      state.value.indexesIds = resp;
    });
  };

  const saveIndex = async (payload: Index) => {
    // Always save to database for advanced fields updates
    return httpClient.post<void, Index>(`${API_PREFIX}/save`, payload).then((response) => {
      state.value.IndexProperties = response;
      return response;
    });
  };

  const documentInfo = (id: string) => {
    //todo call on cosmo to get data
    // httpClient.get<void,any>(`${API_PREFIX}/document`, {
    //   params: {
    //     id: id
    //   }
    // }).then((resp)=> {
    //   console.log('resp----------->', resp)
    // })
  };

  const getDocumentAndChunks = (payload: ChunksPayload) => {
    return httpClient.post<void, IndexDocumentAndChunks>(`${API_PREFIX}/document-chunks`, payload).then((result) => {
      state.value.documentPageImageBase64 = 'data:image/png;base64,' + result.imageBase64;
      sentenceList.value = result.documents.map(mapIndexDocumentToSentence);
      state.value.documentTotalPages = result.totalPages;
      state.value.documentFileName = result.fileName;
    });
  };

  const getIndexIdByName = (indexName: string) => {
    return httpClient
      .post<void, number>(`${API_PREFIX}/get-index-id-by-name`, JSON.stringify(indexName), {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((result) => {
        state.value.newlyCreatedIndex = result;
        return result; // Return the result so it can be used in the calling code
      });
  };

  const getIndexByChatbotId = (chatbotId: number) => {
    return httpClient.post<void, Index>(`${API_PREFIX}/get-index-by-chatbot-name/${chatbotId}`).then((result) => {
      return result;
    });
  };

  const getIndexesLite = () => {
    return httpClient.post<void, IndexLite[]>(`${API_PREFIX}/get-indexes-lite`).then((result) => {
      state.value.indexesLite = result;
    });
  };

  const mapIndexDocumentToSentence = (x: IndexDocument) => {
    return {
      $type: 'Artea.Document.Base.xSentence, Artea.Document.Base',
      id: x.id,
      uri: x.url,
      title: x.title || x.chunkType,
      text: x.content,
      tokens: [],
      type: x.chunkType,
      pageNumber: x.pageNumber || 1,
      positionOnPage: x.positionOnPage || '',
    };
  };

  return {
    getIndexes,
    saveIndex,
    documentInfo,
    getLibraries,
    getIndexById,
    getIndexesIds,
    getIndexesLite,
    getIndexIdByName,
    getIndexByChatbotId,
    getDocumentAndChunks,
    fetchDirectoryContent,
    sendIndexerConfiguration,
  };
};

export default useIndexesStoreActions;
